import { useState, useEffect } from "react";
import { useQuoteCart } from "@/contexts/QuoteCartContext";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ShoppingCart, Package, Trash2, Plus, Minus, Send, Loader2, CheckCircle2, Truck, FlaskConical, Clock, FileDown, X,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function QuoteCartDialog() {
  const {
    cart, showCart, setShowCart, updateQty, setQty, removeItem, submit, clearCart,
    freight, setFreight, totals,
    microTat, setMicroTat, MICRO_TAT_TIERS,
  } = useQuoteCart();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [guest, setGuest] = useState({
    name: "", hospital: "", email: "", phone: "",
    address1: "", address2: "", city: "", state: "", zip: "", notes: "",
    poNumber: "", paymentTerms: "Net 30",
  });

  const handleSubmit = async () => {
    setSubmitting(true);
    const res = await submit(guest);
    setSubmitting(false);
    if (res.ok) setSubmitted(true);
  };

  // Build a branded PDF of the current cart contents using the same template
  // as the admin Backorder Quote (navy header band, Sterile Mate logo, item
  // table, totals stack). Contact / shipping fields are optional — if the
  // visitor hasn't filled them in, the PDF still renders cleanly.
  const handleDownloadPdf = async () => {
    if (cart.length === 0) return;
    setDownloadingPdf(true);
    try {
      const shippingAddress = [
        guest.address1,
        guest.address2,
        [guest.city, guest.state, guest.zip].filter(Boolean).join(", "),
      ].filter(Boolean).join(" · ");
      const payload = {
        contact_name: guest.name || null,
        hospital_name: guest.hospital || null,
        contact_email: guest.email || null,
        contact_phone: guest.phone || null,
        shipping_address: shippingAddress || null,
        line_items: cart.map((i) => ({
          name: i.name,
          erp: i.erp || null,
          quantity: i.qty,
          unit_price: Number(i.unit_price) || 0,
          source: i.source || null,
          is_solm: !!i.is_solm,
          pack: i.pack && typeof i.pack === "object" ? i.pack : null,
          notes: i.notes || null,
        })),
        subtotal: totals.subtotal || 0,
        micro_tat_label: totals.hasMicro ? totals.microTat?.label : null,
        micro_tat_surcharge: totals.microTatSurcharge || 0,
        micro_fees: (totals.microFees || []).map((f) => ({ label: f.label, amount: f.amount })),
        micro_min_top_up: totals.microMinTopUp || 0,
        freight_label: totals.hasOther ? "Freight & handling (20%)" : "Freight",
        freight_amount: totals.hasOther ? (freight.useOwn ? 0 : totals.freightCost) : 0,
        freight_use_own: !!freight.useOwn,
        freight_carrier: freight.carrier || null,
        total: totals.total || 0,
        source: totals.hasMicro ? "microbiology" : "cart",
      };
      const res = await fetch(`${API}/api/public/cart/quote-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SterileMate-Quote-${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("PDF download failed", err);
      alert("Sorry — we couldn't generate the PDF. Please try again or email orders@sterilemate.com.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

  // Lock body scroll + close on ESC when cart page is open
  useEffect(() => {
    if (!showCart) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setShowCart(false); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = original;
      window.removeEventListener("keydown", onKey);
    };
  }, [showCart, setShowCart]);

  return (
    <AnimatePresence>
      {showCart && (
        <motion.div
          className="fixed inset-0 z-[80] bg-slate-50 overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          data-testid="quote-cart-fullscreen"
        >
          {/* Sticky page header */}
          <div className="sticky top-0 z-10 bg-white border-b border-slate-200 shadow-sm">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-md shrink-0">
                  <ShoppingCart className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-amber-700">Quote Cart</p>
                  <h1 className="text-lg sm:text-2xl font-black text-slate-900 truncate" data-testid="quote-cart-title">
                    Your Order · {cart.length} item{cart.length === 1 ? "" : "s"}
                  </h1>
                </div>
              </div>
              <button
                onClick={() => setShowCart(false)}
                className="inline-flex items-center gap-1.5 h-10 px-3 sm:px-4 rounded-lg border border-slate-300 bg-white hover:bg-slate-100 text-slate-700 text-sm font-semibold transition-colors"
                data-testid="quote-cart-close"
                aria-label="Close quote cart"
              >
                <X className="w-4 h-4" />
                <span className="hidden sm:inline">Continue shopping</span>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="quote-cart-dialog">

        {submitted ? (
          <div className="text-center py-8">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">Order Submitted!</h3>
            <p className="text-slate-500 mb-4">
              We&apos;ve emailed a confirmation to <strong>{guest.email}</strong>. Our
              fulfillment team will reply within 2 business hours.
            </p>
            <p className="text-xs text-slate-500 mb-4">
              New customers: credit terms approval can take up to 24 hours. Prepay orders ship immediately on payment receipt.
            </p>
            <Button onClick={() => { setSubmitted(false); clearCart(); setShowCart(false); }}>
              Continue Browsing
            </Button>
          </div>
        ) : (
          <>
            {cart.length === 0 ? (
              <div className="text-center py-8">
                <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Your quote cart is empty</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  {cart.map((item) => {
                    const price = Number(item.unit_price) || 0;
                    const lineTotal = price * item.qty;
                    const uom = item.uom
                      || (typeof item.pack === "string" ? item.pack : item.pack?.display)
                      || (item.is_solm ? "case" : "each");
                    return (
                      <div key={item.erp} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border" data-testid={`cart-line-${item.erp}`}>
                        {item.image
                          ? <img src={item.image} alt="" className="w-14 h-14 object-contain bg-white rounded" />
                          : <div className="w-14 h-14 bg-white rounded flex items-center justify-center"><Package className="w-5 h-5 text-slate-300" /></div>}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate" title={item.name}>{item.name}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">SKU:</span>
                            <span className="text-[11px] font-mono text-slate-900" data-testid={`cart-line-sku-${item.erp}`}>{item.erp}</span>
                            <span className="text-slate-300">·</span>
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">UOM:</span>
                            <span className="text-[11px] font-semibold text-slate-900" data-testid={`cart-line-uom-${item.erp}`}>{uom}</span>
                            {item.source && (
                              <>
                                <span className="text-slate-300">·</span>
                                <span className="text-[10px] uppercase tracking-wider text-slate-500">{item.source}</span>
                              </>
                            )}
                            {price > 0 && <span className="ml-1 text-xs text-emerald-700 font-bold">{money(price)}/{uom}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => updateQty(item.erp, -1)} className="w-8 h-8 rounded bg-slate-200 hover:bg-slate-300 flex items-center justify-center transition-colors" aria-label="decrease" data-testid={`cart-line-decrement-${item.erp}`}>
                            <Minus className="w-3.5 h-3.5" />
                          </button>
                          <input
                            type="number"
                            min={1}
                            step={1}
                            value={item.qty}
                            onChange={(e) => {
                              const n = parseInt(e.target.value, 10);
                              if (Number.isFinite(n) && n >= 1) setQty(item.erp, n);
                              else if (e.target.value === "") setQty(item.erp, 1);
                            }}
                            onFocus={(e) => e.target.select()}
                            onWheel={(e) => e.currentTarget.blur()}
                            className="w-14 h-8 text-center text-sm font-bold border border-slate-300 rounded focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                            aria-label={`Quantity for ${item.name}`}
                            data-testid={`cart-line-qty-${item.erp}`}
                          />
                          <button onClick={() => updateQty(item.erp, 1)} className="w-8 h-8 rounded bg-slate-200 hover:bg-slate-300 flex items-center justify-center transition-colors" aria-label="increase" data-testid={`cart-line-increment-${item.erp}`}>
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="w-24 text-right text-sm font-bold text-slate-900" data-testid={`cart-line-total-${item.erp}`}>
                          {price > 0 ? money(lineTotal) : <span className="text-xs text-slate-400 italic">quoted</span>}
                        </div>
                        <button onClick={() => removeItem(item.erp)} className="text-red-400 hover:text-red-600 transition-colors" aria-label="remove" data-testid={`cart-line-remove-${item.erp}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* ---------- Totals + Freight toggle ---------- */}
                {totals.subtotal > 0 && (
                  <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="cart-totals">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Subtotal ({cart.length} {cart.length === 1 ? "line" : "lines"})</span>
                      <span className="font-bold text-slate-900" data-testid="cart-subtotal">{money(totals.subtotal)}</span>
                    </div>

                    {/* MICROBIOLOGY LAB FEES — appears only when cart contains A&B Labs tests */}
                    {totals.hasMicro && (
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 space-y-2" data-testid="cart-micro-fees">
                        <div className="flex items-center gap-2 mb-1">
                          <FlaskConical className="w-3.5 h-3.5 text-emerald-700" />
                          <span className="text-xs font-bold text-emerald-800 uppercase tracking-wider">A&amp;B Labs · per-job charges</span>
                        </div>

                        {/* TAT tier picker */}
                        <div className="mb-2">
                          <Label className="text-[11px] text-slate-600 flex items-center gap-1.5 mb-1">
                            <Clock className="w-3 h-3" /> Turnaround time
                          </Label>
                          <select
                            value={microTat}
                            onChange={(e) => setMicroTat(e.target.value)}
                            data-testid="cart-micro-tat"
                            className="w-full text-sm rounded-md border border-emerald-200 bg-white px-2 py-1.5 focus:ring-2 focus:ring-emerald-500/40"
                          >
                            {MICRO_TAT_TIERS.map((t) => (
                              <option key={t.key} value={t.key}>
                                {t.label}{t.pct > 0 ? ` (+${(t.pct * 100).toFixed(0)}%)` : ""}
                              </option>
                            ))}
                          </select>
                        </div>

                        {totals.microTatSurcharge > 0 && (
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-600">TAT rush surcharge (+{(totals.microTat.pct * 100).toFixed(0)}%)</span>
                            <span className="font-semibold text-slate-900" data-testid="cart-micro-rush">{money(totals.microTatSurcharge)}</span>
                          </div>
                        )}
                        {totals.microFees.map((f) => (
                          <div key={f.key} className="flex items-center justify-between text-xs">
                            <span className="text-slate-600">{f.label}</span>
                            <span className="font-semibold text-slate-900" data-testid={`cart-micro-fee-${f.key}`}>{money(f.amount)}</span>
                          </div>
                        ))}
                        {totals.microMinTopUp > 0 && (
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-600 italic">$200 minimum-invoice top-up</span>
                            <span className="font-semibold text-amber-700" data-testid="cart-micro-min">{money(totals.microMinTopUp)}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Freight panel — hidden when cart contains ONLY microbiology
                        tests (A&B Labs delivery is already covered by per-job fees) */}
                    {totals.hasOther && (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={freight.useOwn}
                          onChange={(e) => setFreight({ ...freight, useOwn: e.target.checked })}
                          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                          data-testid="cart-own-freight"
                        />
                        <div className="flex-1">
                          <span className="block text-sm font-semibold text-slate-900">
                            We have our own freight forwarder
                          </span>
                          <span className="block text-xs text-slate-500 mt-0.5">
                            Removes the 20% Sterile Mate freight &amp; handling charge. You provide the carrier &amp; account.
                          </span>
                        </div>
                      </label>

                      {freight.useOwn && (
                        <div className="mt-3 grid grid-cols-1 gap-2" data-testid="cart-freight-fields">
                          <Input
                            placeholder="Freight forwarder / carrier name *"
                            value={freight.carrier}
                            onChange={(e) => setFreight({ ...freight, carrier: e.target.value })}
                            data-testid="cart-freight-carrier"
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              placeholder="Account # (optional)"
                              value={freight.account}
                              onChange={(e) => setFreight({ ...freight, account: e.target.value })}
                              data-testid="cart-freight-account"
                            />
                            <Input
                              placeholder="Carrier contact / phone"
                              value={freight.contact}
                              onChange={(e) => setFreight({ ...freight, contact: e.target.value })}
                              data-testid="cart-freight-contact"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    )}

                    {totals.hasOther && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600 flex items-center gap-1.5">
                        <Truck className="w-3.5 h-3.5" /> Freight &amp; handling (20%)
                      </span>
                      <span className={`font-bold ${freight.useOwn ? "text-slate-400 italic" : "text-slate-900"}`} data-testid="cart-freight-cost">
                        {freight.useOwn ? "provided by hospital" : money(totals.freightCost)}
                      </span>
                    </div>
                    )}

                    {/* Helpful badge when cart contains ONLY micro tests — clarifies
                        that the 20% markup is intentionally skipped, not missing. */}
                    {!totals.hasOther && totals.hasMicro && (
                      <div className="flex items-center justify-between text-xs text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded-md px-3 py-2">
                        <span className="flex items-center gap-1.5">
                          <CheckCircle2 className="w-3.5 h-3.5" /> 20% freight markup does not apply
                        </span>
                        <span className="font-semibold">A&amp;B Labs delivery included</span>
                      </div>
                    )}

                    <div className="flex items-center justify-between border-t border-slate-200 pt-3">
                      <span className="text-base font-bold text-slate-900">Estimated Total</span>
                      <span className="text-xl font-extrabold text-emerald-700" data-testid="cart-grand-total">
                        {money(totals.total)}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500">
                      Pricing &amp; freight confirmed by Sterile Mate at PO. Manufacturer-direct, non-GPO.
                    </p>
                  </div>
                )}

                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold mb-3">Your Information</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Name *</Label><Input value={guest.name} onChange={(e) => setGuest({ ...guest, name: e.target.value })} placeholder="John Smith" className="mt-1" required data-testid="cart-name" /></div>
                    <div><Label className="text-xs">Hospital / Facility *</Label><Input value={guest.hospital} onChange={(e) => setGuest({ ...guest, hospital: e.target.value })} placeholder="Memorial Hermann" className="mt-1" required data-testid="cart-hospital" /></div>
                    <div><Label className="text-xs">Email *</Label><Input type="email" value={guest.email} onChange={(e) => setGuest({ ...guest, email: e.target.value })} placeholder="john@hospital.org" className="mt-1" required data-testid="cart-email" /></div>
                    <div><Label className="text-xs">Phone *</Label><Input value={guest.phone} onChange={(e) => setGuest({ ...guest, phone: e.target.value })} placeholder="(281) 555-0123" className="mt-1" required data-testid="cart-phone" /></div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold mb-3">Shipping Address *</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2"><Label className="text-xs">Street Address *</Label><Input value={guest.address1} onChange={(e) => setGuest({ ...guest, address1: e.target.value })} placeholder="1234 Medical Center Blvd" className="mt-1" required data-testid="cart-address1" /></div>
                    <div className="col-span-2"><Label className="text-xs">Suite / Unit</Label><Input value={guest.address2} onChange={(e) => setGuest({ ...guest, address2: e.target.value })} placeholder="Suite 200 / Building B" className="mt-1" data-testid="cart-address2" /></div>
                    <div><Label className="text-xs">City *</Label><Input value={guest.city} onChange={(e) => setGuest({ ...guest, city: e.target.value })} placeholder="Houston" className="mt-1" required data-testid="cart-city" /></div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label className="text-xs">State *</Label><Input value={guest.state} onChange={(e) => setGuest({ ...guest, state: e.target.value.toUpperCase().slice(0,2) })} placeholder="TX" maxLength={2} className="mt-1" required data-testid="cart-state" /></div>
                      <div><Label className="text-xs">ZIP *</Label><Input value={guest.zip} onChange={(e) => setGuest({ ...guest, zip: e.target.value })} placeholder="77030" className="mt-1" required data-testid="cart-zip" /></div>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    Purchase Order &amp; Payment Terms
                    <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 ring-1 ring-amber-200 px-2 py-0.5 rounded-full">required</span>
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">PO Number *</Label>
                      <Input
                        value={guest.poNumber}
                        onChange={(e) => setGuest({ ...guest, poNumber: e.target.value })}
                        placeholder="PO-12345"
                        className="mt-1"
                        required
                        data-testid="cart-po-number"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Payment Terms *</Label>
                      <select
                        value={guest.paymentTerms}
                        onChange={(e) => setGuest({ ...guest, paymentTerms: e.target.value })}
                        className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent h-10"
                        data-testid="cart-payment-terms"
                      >
                        <option value="Net 30">Net 30 (existing customers)</option>
                        <option value="Prepay">Prepay (ACH / credit card / check)</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-3 rounded-md bg-amber-50 ring-1 ring-amber-200 px-3 py-2 text-[12px] text-amber-900 leading-snug" data-testid="cart-new-customer-notice">
                    <strong>New customer?</strong> If your facility hasn&apos;t ordered through Sterile Mate before, we&apos;ll open a new hospital account from this order. <strong>Net 30 credit terms require up to 24 hours for approval.</strong> Prepay orders ship as soon as payment is received.
                  </div>
                </div>

                <div className="border-t pt-4">
                  <Label className="text-sm font-semibold">Notes / Special Instructions</Label>
                  <textarea
                    value={guest.notes}
                    onChange={(e) => setGuest({ ...guest, notes: e.target.value })}
                    placeholder="PO number, preferred delivery date, substitutions allowed, urgency, or anything else our team should know…"
                    rows={3}
                    className="mt-2 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-y"
                    data-testid="cart-notes"
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    type="button"
                    onClick={handleDownloadPdf}
                    disabled={downloadingPdf || cart.length === 0}
                    variant="outline"
                    className="sm:w-auto h-12 border-slate-300 text-slate-800 hover:bg-slate-50 font-semibold"
                    data-testid="download-quote-pdf-btn"
                  >
                    {downloadingPdf
                      ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Building PDF…</>
                      : <><FileDown className="w-4 h-4 mr-2" />Download Quote PDF</>
                    }
                  </Button>
                  <Button onClick={handleSubmit} disabled={submitting} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white h-12 font-semibold" data-testid="submit-quote-btn">
                    {submitting
                      ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</>
                      : <><Send className="w-4 h-4 mr-2" />Submit Order ({cart.length} {cart.length === 1 ? "item" : "items"})</>
                    }
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
